import { DEG2RAD } from '../../utils.js';
import { WeaponMode } from '../../weapons/WeaponSystem.js';
import { CameraMode } from '../../camera/CameraManager.js';

/**
 * FT-102 — Close Air Support.
 *
 * Two weapon phases flown back to back on one sortie. First the gun: a pair of
 * halted BTR-80s on the open flats of tile G8, strafed with a talked-through
 * attack (select, roll in, burst, break) and then a second target the player
 * sets up alone. Then the Maverick: climb, push east over the ridge, and take
 * two more BTRs off the apron of the disused airstrip in the south-west corner
 * of tile E11 from standoff, on the TV.
 *
 * ── Why the geometry is what it is ──────────────────────────────────────────
 * Everything is authored in raw world XZ rather than the runway frame FT-101
 * uses: the airfield is 20 km behind the player at spawn and never relevant
 * again, so runway-relative coordinates would only obscure the numbers.
 *
 * The player spawns on heading 225 (this sim's compass runs counter-clockwise:
 * 0 = N, 90 = W, 180 = S, 270 = E — so 225 is south-east) 19 km short of the
 * first gun target, which sits exactly on that extended centreline. That leg is
 * the lesson's budget: at the 154 m/s spawn speed it is a little over two
 * minutes, which is what the intro, the target call, the weapon selection and
 * the gun-employment brief have to fit inside before the roll-in.
 *
 * The second gun target is 1,300 m off to the side of the run-in axis rather
 * than further down it, so a single pass can't take both and the player has to
 * fly a genuine re-attack for it.
 *
 * The Maverick leg runs east-north-east and crosses the mountains on tile F10,
 * which is the reason for the climb: that ridge isn't scenery, it's the thing
 * that would otherwise sit in the seeker's line of sight. Sampled against the
 * heightmap, the ray from the seeker to a vehicle on the airstrip clears the
 * ridge out to about six miles from 4,000 ft, but is blocked from three miles
 * out at 2,000 ft — so the altitude is a hard requirement, not a flourish, and
 * the brief says why. Because that leg is also only two minutes long, the climb
 * is *ordered* immediately but *checked* four objectives later, so it runs
 * underneath the weapon setup instead of consuming the whole transit.
 *
 * A lock achieved from outside that envelope would be luck, so the lock
 * objective waits on a real lock (`lockedEntity` being one of ours) rather than
 * on range: a seeker looking at rock simply never satisfies it, and a hint
 * tells the player that's what they're looking at.
 *
 * ── Targets ─────────────────────────────────────────────────────────────────
 * Both pairs are placed by the script rather than by the scenario's `spawns`,
 * each on the objective *before* the one that shoots them — see the `spawn`
 * field in TrainingMission. That keeps the Maverick targets off the HUD during
 * the gun phase, and it is also the only safe ordering: an unspawned id reads
 * as already destroyed, so a `destroy` objective that spawned its own targets
 * would clear itself the instant it came up.
 *
 * Voice-over: same convention as FT-101 — recordings live under
 * public/assets/sound/voice/closeAirSupport/ as `<audio>_captions`,
 * `<audio>_complete` and `<audio>_hint_<id>`, with the `audio` keys carrying
 * the script's running order so the files sort the way the lesson plays. None
 * are recorded yet, so for now the authored caption `duration`s are the literal
 * on-screen times rather than relative weights (see training/CaptionPlayer.js),
 * which is why the pacing above is costed in those numbers.
 */

// ── Gun range (tile G8) ─────────────────────────────────────────────────────
// Open, flat desert — the heightmap runs 170-185 m across the whole tile, and
// the only culture is a small settlement in the north-west corner, well clear
// of both targets. GUN_1 sits 19 km down the spawn heading; GUN_2 is 1,300 m to
// its north-east, square to the run-in.
const GUN_GROUND = 170;            // m MSL — measured, and only seats HUD markers
const GUN_1 = { x: 8435, z: 15435 };
const GUN_2 = { x: 9354, z: 14516 };

// ── Maverick range (tile E11, south-west corner) ────────────────────────────
// The disused airstrip. Both vehicles are parked on the apron, 470 m apart —
// far enough that the seeker's 30 m acquisition radius tells them apart, close
// enough that both sit in the optic's default 6° field at engagement range.
const FIELD_GROUND = 229;          // m MSL — measured, as above
const MAV_1 = { x: 24500, z: 5450 };
const MAV_2 = { x: 24900, z: 5700 };
// Aim point between the two, used for the HUD marker and the "nose on it" test.
const FIELD = { x: 24700, z: 5575 };

const MAV_IDS = ['casMaverick1', 'casMaverick2'];

// Maverick release perch — see the ridge note in the header.
const STRIKE_ALT = 1219;           // m MSL (4,000 ft)

// Spawn entries, in the scenario `spawns` shape. `state: 'loiter'` is what
// makes them stationary: the driver holds desiredSpeed at zero and leaves the
// heading alone, so they sit as emplaced instead of wandering off cross-country
// the way an `explore` vehicle would. No `useRoads` — neither range is near the
// road network, and snapping would drag them tens of kilometres.
const gunTargets = [
  { type: 'btr80', id: 'casGun1', x: GUN_1.x, y: 'agl:0', z: GUN_1.z, heading: 20, initialHealth: 100, state: 'loiter' },
  { type: 'btr80', id: 'casGun2', x: GUN_2.x, y: 'agl:0', z: GUN_2.z, heading: 250, initialHealth: 100, state: 'loiter' },
];
const maverickTargets = [
  { type: 'btr80', id: MAV_IDS[0], x: MAV_1.x, y: 'agl:0', z: MAV_1.z, heading: 90, initialHealth: 100, state: 'loiter' },
  { type: 'btr80', id: MAV_IDS[1], x: MAV_2.x, y: 'agl:0', z: MAV_2.z, heading: 100, initialHealth: 100, state: 'loiter' },
];

// Marker volumes. Nothing in this mission is actually captured — every waypoint
// is a `guide`, since a strafing pass and a standoff shot are both judged on
// what the player does, not on flying through a hoop — so these only size the
// HUD marker's "you are here" band.
const TARGET_MARK = { radius: 400, vertical: 600 };
const FIELD_MARK = { radius: 900, vertical: 1500 };

const seekerOf = (c) => c.app?.maverickSeeker ?? null;
const weaponOf = (c) => c.app?.weaponSystem ?? null;

// ── Shared hints ────────────────────────────────────────────────────────────
// Three failure modes recur across every attack objective, so they're built
// rather than repeated, and each names its own `audio` key so the mission needs
// one recording per line instead of one per objective that reuses it.

// Pressing an attack into the dirt is the one way to lose the airframe here, so
// this fires fast and often: a short arming window, a short cooldown, and no
// practical cap, because the player may fly half a dozen passes.
function pullUpHint() {
  return {
    id: 'pullUp',
    when: (c) => c.airborne && c.altitudeAGL < 120 && c.verticalSpeed < -8,
    after: 1.5, cooldown: 7, max: 99,
    audio: 'pullUpHint_pullUp',
    caption: 'Altitude, Hawg One — get the nose up.',
  };
}

// Lost the target astern. Keyed off angle as well as range, the same way
// FT-101's circuit hints are, so a long but correct run-in doesn't nag.
function reattackHint() {
  return {
    id: 'reattack',
    when: (c) => c.waypointRange > 4000 && Math.abs(c.waypointHeadingError) > 70 * DEG2RAD,
    after: 12, cooldown: 20,
    audio: 'reattackHint_reattack',
    caption: "Target's off the nose — bring her round and set up another pass.",
  };
}

// Shooting from outside the gun's useful envelope. The rounds fall away long
// before 4 km, so this is a real correction and not a style note.
function longShotHint() {
  return {
    id: 'longShot',
    when: (c) => !!weaponOf(c)?.firing && c.waypointRange > 3500,
    after: 1.2, cooldown: 12,
    audio: 'longShotHint_longShot',
    caption: "Too far out — those rounds are falling short. Hold your fire until you're in range.",
  };
}

export default {
  id: 'closeAirSupport',
  title: 'FT-102 · CLOSE AIR SUPPORT',

  objectives: [
    // ── 1. Intro ────────────────────────────────────────────────────────────
    {
      id: 'intro',
      audio: '01_intro',
      type: 'info',
      objective: '',
      audioCaptions: [
        { caption: "Hawg One, instructor's up. Today is close air support — putting ordnance on targets for the guys on the ground.", duration: 7 },
        { caption: 'Two phases. The gun first, then the Maverick.', duration: 3.5 },
      ],
    },

    // ── 2. Targets designated ───────────────────────────────────────────────
    // Carries the gun pair onto the range. An `info` beat is never pre-empted
    // by _alreadyDone, so the spawn is guaranteed to happen exactly once.
    {
      id: 'designate',
      audio: '02_designate',
      type: 'info',
      objective: '',
      spawn: gunTargets,
      waypoint: { ...GUN_1, y: GUN_GROUND, label: 'TGT 1', guide: true, ...TARGET_MARK },
      audioCaptions: [
        { caption: "Range control's passed us two targets — a pair of BTRs sitting out on the flats, ten miles off the nose.", duration: 7 },
        { caption: "They're marked on your HUD. Work the first one; we'll come back for the second.", duration: 5.5 },
      ],
    },

    // ── 3. Select the gun ───────────────────────────────────────────────────
    {
      id: 'selectGun',
      audio: '03_selectGun',
      type: 'task',
      objective: 'Select the GAU-8 cannon  [T]',
      waypoint: { ...GUN_1, y: GUN_GROUND, label: 'TGT 1', guide: true, ...TARGET_MARK },
      audioCaptions: [
        { caption: "Let's get the gun up. Tap T to cycle the weapon selector until the thirty millimetre cannon comes round.", duration: 6 },
      ],
      complete: (c) => weaponOf(c)?.mode === WeaponMode.GAU8,
      hints: [
        { id: 'cycle', after: 10, cooldown: 14, caption: 'Weapon selector is T — cycle it round to the cannon.' },
      ],
    },

    // ── 4. Gun employment brief ─────────────────────────────────────────────
    // Deliberately the longest block in the mission, and deliberately an `info`
    // with nothing to do: it has to run its half-minute while the player is
    // still four or five miles out, so the roll-in lands on a briefed pilot.
    {
      id: 'gunBrief',
      audio: '04_gunBrief',
      type: 'info',
      objective: '',
      waypoint: { ...GUN_1, y: GUN_GROUND, label: 'TGT 1', guide: true, ...TARGET_MARK },
      audioCaptions: [
        { caption: "That's the Avenger — thirty millimetre, sixty-five rounds a second. It will tear an armored vehicle apart.", duration: 7 },
        { caption: 'Roll in shallow, ten or fifteen degrees nose low, and put the pipper on the vehicle.', duration: 6 },
        { caption: 'The pipper already allows for the time of flight, so all you have to do is hold it steady.', duration: 6 },
        { caption: 'Hold the right mouse button on the way in — it zooms you up and stabilizes the aircraft for gunnery.', duration: 6.5 },
        { caption: 'Short bursts. A second at a time, no more. Then get off the target.', duration: 5 },
      ],
    },

    // ── 5. First strafing pass ──────────────────────────────────────────────
    // Cleared by the trigger, not by the kill: this step is "take the shot",
    // and the kill is objective 7. Held for a third of a second so a stray
    // click on the way in doesn't read as a burst.
    {
      id: 'firstPass',
      audio: '05_firstPass',
      type: 'task',
      objective: 'Roll in on target one and fire a burst',
      waypoint: { ...GUN_1, y: GUN_GROUND, label: 'TGT 1', guide: true, ...TARGET_MARK },
      audioCaptions: [
        { caption: 'Target one is yours. Roll in, pipper on the vehicle, and give me a burst.', duration: 5.5 },
      ],
      complete: (c) => !!weaponOf(c)?.firing && c.waypointRange < 2200,
      hold: 0.35,
      hints: [
        pullUpHint(),
        longShotHint(),
        {
          id: 'track',
          when: (c) => c.waypointRange < 3500 && Math.abs(c.waypointHeadingError) > 20 * DEG2RAD,
          after: 4, cooldown: 10,
          caption: 'Nose is off — walk the pipper onto him before you shoot.',
        },
      ],
    },

    // ── 6. Tactical break ───────────────────────────────────────────────────
    // Flying the pass out the far side is how pilots hit the ground, so the
    // break is its own step rather than an afterthought. Satisfied by bank plus
    // the target being genuinely astern (>100° off the nose), which a player who
    // simply flies straight through never reaches.
    {
      id: 'break',
      audio: '06_break',
      type: 'task',
      objective: 'Break off the target',
      waypoint: { ...GUN_1, y: GUN_GROUND, label: 'TGT 1', guide: true, ...TARGET_MARK },
      audioCaptions: [
        { caption: 'Break! Hard turn, nose up, get away from the target.', duration: 5 },
        { caption: 'Never fly through your own frag, and never press an attack into the dirt.', duration: 5 },
      ],
      complete: (c) => Math.abs(c.bank) > 50 * DEG2RAD
        && Math.abs(c.waypointHeadingError) > 100 * DEG2RAD,
      hold: 0.3,
      hints: [
        pullUpHint(),
        { id: 'pull', after: 6, cooldown: 12, caption: 'Bank it up and pull off the target, Hawg One.' },
      ],
    },

    // ── 7. Kill target one ──────────────────────────────────────────────────
    // Skipped silently if the first burst already did it — which is what
    // _alreadyDone is for, and the common case for a steady pass.
    {
      id: 'killOne',
      audio: '07_killOne',
      type: 'destroy',
      objective: 'Destroy target one',
      targets: ['casGun1'],
      waypoint: { ...GUN_1, y: GUN_GROUND, label: 'TGT 1', guide: true, ...TARGET_MARK },
      audioCaptions: [
        { caption: "He's still up. Come back round and finish him — same pattern. Roll in, short burst, break off.", duration: 7 },
      ],
      hints: [pullUpHint(), reattackHint(), longShotHint()],
      onCompleteCaptions: [
        { caption: 'Target one destroyed. Good gun.', duration: 3 },
      ],
    },

    // ── 8. Kill target two, unassisted ──────────────────────────────────────
    {
      id: 'killTwo',
      audio: '08_killTwo',
      type: 'destroy',
      objective: 'Destroy target two',
      targets: ['casGun2'],
      waypoint: { ...GUN_2, y: GUN_GROUND, label: 'TGT 2', guide: true, ...TARGET_MARK },
      audioCaptions: [
        { caption: "Target two, north-east of the first one. This one's all yours — set up your own attack and take him out.", duration: 7 },
      ],
      hints: [pullUpHint(), reattackHint(), longShotHint()],
      onCompleteCaptions: [
        { caption: "Both vehicles down. That's the gun phase complete.", duration: 4 },
      ],
    },

    // ── 9. New tasking ──────────────────────────────────────────────────────
    // Brings the airstrip pair onto the field, four steps ahead of the
    // `destroy` that needs them to already exist. Also where the climb is
    // *ordered*, several objectives before it is *checked* — see step 13.
    {
      id: 'tasking',
      audio: '09_tasking',
      type: 'info',
      objective: '',
      spawn: maverickTargets,
      waypoint: { ...FIELD, y: FIELD_GROUND, label: 'AIRSTRIP', guide: true, ...FIELD_MARK },
      audioCaptions: [
        { caption: 'New tasking. Disused airstrip twelve miles north-east — recce has two vehicles parked on the apron.', duration: 7.5 },
        { caption: "That's a Maverick job. We shoot this one from standoff instead of flying over the top of it.", duration: 6.5 },
        { caption: "Turn for it and start climbing now — I'll get you set up on the way across.", duration: 5.5 },
      ],
    },

    // ── 10. Cockpit view ────────────────────────────────────────────────────
    // The seeker feed is a shader on the physical `_TV` plane in the cockpit and
    // isn't rendered at all from the chase camera, so this is a hard
    // prerequisite rather than a preference. Skipped silently for a player who
    // is already inside, which is most of them.
    {
      id: 'cockpit',
      audio: '10_cockpit',
      type: 'task',
      objective: 'Switch to the cockpit view  [Enter]',
      waypoint: { ...FIELD, y: STRIKE_ALT, label: 'AIRSTRIP', guide: true, ...FIELD_MARK },
      audioCaptions: [
        { caption: "You'll want to be inside for this — the Maverick picture comes up on the TV display in the cockpit.", duration: 6 },
      ],
      complete: (c) => c.app?.cameraManager?.mode === CameraMode.COCKPIT,
      hints: [
        { id: 'enter', after: 8, cooldown: 14, caption: 'Press Enter to get back in the cockpit.' },
      ],
    },

    // ── 11. Select the Maverick ─────────────────────────────────────────────
    {
      id: 'selectMaverick',
      audio: '11_selectMaverick',
      type: 'task',
      objective: 'Select the AGM-65 Maverick  [T]',
      waypoint: { ...FIELD, y: STRIKE_ALT, label: 'AIRSTRIP', guide: true, ...FIELD_MARK },
      audioCaptions: [
        { caption: 'Cycle the selector round to the Maverick.', duration: 3.5 },
      ],
      complete: (c) => weaponOf(c)?.mode === WeaponMode.AGM65,
      hints: [
        { id: 'cycle', after: 9, cooldown: 14, caption: 'Keep tapping T — the Maverick is the last station in the cycle.' },
      ],
    },

    // ── 12. Seeker brief ────────────────────────────────────────────────────
    // An `info`, so it always plays: the three steps after it only make sense
    // once the player knows the seeker starts caged to the boresight.
    {
      id: 'seekerBrief',
      audio: '12_seekerBrief',
      type: 'info',
      objective: '',
      waypoint: { ...FIELD, y: STRIKE_ALT, label: 'AIRSTRIP', guide: true, ...FIELD_MARK },
      audioCaptions: [
        { caption: 'The seeker sits on the boresight until you take hold of it — wherever the nose points is what the TV sees.', duration: 7 },
        { caption: 'So line the target up roughly with the aircraft first, then take the picture and refine it.', duration: 6 },
      ],
    },

    // ── 13. On the perch ────────────────────────────────────────────────────
    // Checked here rather than straight after the tasking call so the climb
    // runs *underneath* the three steps above instead of blocking them: the leg
    // is only about two minutes, and a climb that owned it outright would leave
    // no room to brief the shot. A player who took the "climb now" call
    // seriously usually arrives with this already satisfied, and it is skipped.
    {
      id: 'climb',
      audio: '13_climb',
      type: 'task',
      objective: 'Climb to 4,000 ft',
      waypoint: { ...FIELD, y: STRIKE_ALT, label: 'AIRSTRIP', guide: true, ...FIELD_MARK },
      audioCaptions: [
        { caption: "I want four thousand feet before you shoot — that's what gets the seeker a clean look over the ridge.", duration: 6.5 },
      ],
      complete: (c) => c.altitude >= STRIKE_ALT - 15,
      hints: [
        pullUpHint(),
        {
          id: 'terrain',
          when: (c) => c.airborne && c.altitudeAGL < 300,
          after: 5, cooldown: 14,
          caption: "Watch the rocks — there's high ground on this heading.",
        },
        {
          id: 'keepClimbing',
          when: (c) => c.altitude < STRIKE_ALT - 200,
          after: 14, cooldown: 20,
          caption: 'Keep the climb in, Hawg One. Four thousand.',
        },
      ],
    },

    // ── 14. Nose on the target area ─────────────────────────────────────────
    // Range gate set just inside the seeker's own eight-mile limit rather than
    // at the briefed release range: the point is to hand the player the seeker
    // early and let them acquire as they close, not to make them fly a gate.
    {
      id: 'boresight',
      audio: '14_boresight',
      type: 'task',
      objective: 'Put the airstrip on the nose',
      waypoint: { ...FIELD, y: FIELD_GROUND, label: 'AIRSTRIP', guide: true, ...FIELD_MARK },
      audioCaptions: [
        { caption: 'Centre the airstrip up and press in. Looking for about five miles before you shoot.', duration: 5.5 },
      ],
      complete: (c) => c.waypointRange < 11000 && Math.abs(c.waypointHeadingError) < 12 * DEG2RAD,
      hints: [
        {
          id: 'steer',
          when: (c) => Math.abs(c.waypointHeadingError) > 25 * DEG2RAD,
          after: 8, cooldown: 15,
          caption: "Airstrip's marked on the HUD — bring the nose round onto it.",
        },
      ],
    },

    // ── 15. Take the seeker ─────────────────────────────────────────────────
    // Right mouse is hold-to-aim, not a toggle: letting go re-cages the seeker
    // to the boresight and throws the setup away, which is far and away the
    // commonest way to lose a Maverick pass. The briefing says so, and the next
    // objective carries a hint that catches it happening.
    {
      id: 'uncage',
      audio: '15_uncage',
      type: 'task',
      objective: 'Hold right mouse to take the seeker',
      waypoint: { ...FIELD, y: FIELD_GROUND, label: 'AIRSTRIP', guide: true, ...FIELD_MARK },
      audioCaptions: [
        { caption: 'Hold the right mouse button down. That hands you the seeker and pins it to the ground, so the picture holds still while you fly.', duration: 8 },
        { caption: 'Keep it held. Mouse slews the crosshair, scroll wheel zooms the optic.', duration: 4.5 },
      ],
      complete: (c) => !!seekerOf(c)?.aiming,
      hints: [
        { id: 'hold', after: 8, cooldown: 13, caption: 'Right mouse, and hold it down.' },
      ],
    },

    // ── 16. Acquire a lock ──────────────────────────────────────────────────
    // Gated on a real lock rather than on range. The seeker reports "in range"
    // against whatever its ray happens to land on, so a player looking at the
    // ridge would otherwise be told they were finished; `lockedEntity` is the
    // same test the missile itself runs at launch, so this clears on exactly
    // the condition that makes the shot work.
    {
      id: 'lock',
      audio: '16_lock',
      type: 'task',
      objective: 'Lock a target on the TV',
      waypoint: { ...FIELD, y: FIELD_GROUND, label: 'AIRSTRIP', guide: true, ...FIELD_MARK },
      audioCaptions: [
        { caption: "Find them on the TV. The seeker's infrared, so anything warm reads white — they'll be two bright shapes on the ground.", duration: 7 },
        { caption: 'Walk the crosshair onto one of them. When the small crosshair appears, the missile has him locked.', duration: 6 },
      ],
      complete: (c) => {
        const s = seekerOf(c);
        return !!s?.inRange && MAV_IDS.includes(s.lockedEntity?.trainingId);
      },
      hints: [
        {
          id: 'released',
          when: (c) => !seekerOf(c)?.aiming,
          after: 3, cooldown: 10,
          caption: "You've let the seeker go — right mouse, and keep it held.",
        },
        {
          id: 'zoom',
          when: (c) => !!seekerOf(c)?.aiming && !seekerOf(c)?.lockedEntity,
          after: 20, cooldown: 24,
          caption: 'Zoom the optic in with the scroll wheel — it makes them a lot easier to pick out.',
        },
      ],
    },

    // ── 17. The strike ──────────────────────────────────────────────────────
    // One objective for both vehicles rather than two in sequence, because the
    // player picks which one the crosshair finds first and an ordered pair
    // would sit there waiting on whichever they left.
    {
      id: 'strike',
      audio: '17_strike',
      type: 'destroy',
      objective: 'Destroy both airstrip targets',
      targets: MAV_IDS,
      waypoint: { ...FIELD, y: FIELD_GROUND, label: 'AIRSTRIP', guide: true, ...FIELD_MARK },
      audioCaptions: [
        { caption: "Locked — fire! The missile flies itself from here, it doesn't need you any more.", duration: 6.5 },
        { caption: 'Stay on the TV and watch it run in, then slew across to the second vehicle and do it again.', duration: 6.5 },
        { caption: "You've got rounds for both in one pass. No need to come back round.", duration: 4.5 },
      ],
      hints: [
        pullUpHint(),
        {
          id: 'shoot',
          when: (c) => !!seekerOf(c)?.inRange && MAV_IDS.includes(seekerOf(c)?.lockedEntity?.trainingId),
          after: 7, cooldown: 13,
          caption: "You're locked up — left click to launch.",
        },
        {
          id: 'overflying',
          when: (c) => c.waypointRange < 2500,
          after: 2, cooldown: 20,
          caption: "You're going through the target. Come off dry, swing round and set it up again.",
        },
        {
          id: 'secondTarget',
          when: (c) => !seekerOf(c)?.aiming,
          after: 9, cooldown: 16,
          caption: "Second vehicle's still sitting there — take the seeker again and slew across to him.",
        },
      ],
    },

    // ── 18. Debrief ─────────────────────────────────────────────────────────
    {
      id: 'debrief',
      audio: '18_debrief',
      type: 'info',
      objective: '',
      audioCaptions: [
        { caption: "Both vehicles destroyed. That's gun and Maverick, start to finish.", duration: 5 },
        { caption: 'Close air support complete. Nice work, Hawg One.', duration: 4 },
      ],
    },
  ],
};
